/* -----------------------------------------------------
   2) Pools of question generators + final function
----------------------------------------------------- */

/* ---------- misc helpers ---------- */
const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));

function getQuestionPool(topic) {
  if (topic === "ratios") {
    return [
      {
        question: () => {
          // Only create ratios that can be simplified (gcd > 1)
          let a, b, g;
          do {
            a = Math.floor(Math.random() * 10) + 2;
            b = Math.floor(Math.random() * 10) + 2;
            g = gcd(a, b);
          } while (g === 1); // keep picking until GCD > 1

          const simplerA = a / g;
          const simplerB = b / g;
          const correct = `${simplerA}:${simplerB}`;

          // Now generate plausible distractors
          function randomRatioString() {
            while (true) {
              const x = Math.floor(Math.random() * 8) + 2;
              const y = Math.floor(Math.random() * 8) + 2;
              const R = `${x}:${y}`;
              if (R !== correct) {
                return R;
              }
            }
          }
          const d1 = randomRatioString();
          let d2;
          do {
            d2 = randomRatioString();
          } while (d2 === d1);

          return {
            q: `Simplify the ratio: ${a}:${b}`,
            a: correct,
            distract: [d1, d2]
          };
        }
      }
    ];
  } else if (topic === "algebra") {
    return [
      {
        question: () => {
          const a = Math.floor(Math.random() * 5) + 1;
          const b = Math.floor(Math.random() * 11) - 5;
          const c = Math.floor(Math.random() * 10) + 1;
          const sol = (c - b) / a;
          return {
            q: `Solve for x: ${a}x + ${b} = ${c}`,
            a: sol.toFixed(1),
            distract: [
              (sol + 1).toFixed(1),
              (sol - 1).toFixed(1)
            ]
          };
        }
      },
      {
        question: () => {
          const eqB = Math.floor(Math.random() * 5) + 1;
          const sum = Math.floor(Math.random() * 15) + 3;
          const xVal = (sum - eqB) * 2;
          return {
            q: `If x/2 + ${eqB} = ${sum}, what is x?`,
            a: xVal,
            distract: [xVal + 1, xVal - 1]
          };
        }
      }
    ];
  } else if (topic === "integers") {
    return [
      {
        question: () => {
          const x = Math.floor(Math.random() * 21) - 10;
          const y = Math.floor(Math.random() * 21) - 10;
          const sum = x + y;
          return {
            q: `What is ${x} + ${y}?`,
            a: sum,
            distract: [sum + 1, sum - 1]
          };
        }
      },
      {
        question: () => {
          const x = Math.floor(Math.random() * 21) - 10;
          const y = Math.floor(Math.random() * 21) - 10;
          const diff = x - y;
          return {
            q: `What is ${x} - ${y}?`,
            a: diff,
            distract: [diff + 1, diff - 1]
          };
        }
      }
    ];
  } else if (topic === "decimals") {
    return [
      {
        question: () => {
          const d1 = (Math.random() * 9 + 0.1).toFixed(2);
          const d2 = (Math.random() * 9 + 0.1).toFixed(2);
          const total = (parseFloat(d1) + parseFloat(d2)).toFixed(2);
          return {
            q: `Add: ${d1} + ${d2} = ?`,
            a: total,
            distract: [
              (parseFloat(total) + 0.5).toFixed(2),
              (parseFloat(total) + 1.0).toFixed(2)
            ]
          };
        }
      },
      {
        question: () => {
          const perc = Math.floor(Math.random() * 21) + 10; // 10..30
          const val = Math.floor(Math.random() * 30) + 5;   // 5..34
          const ans = ((perc / 100) * val).toFixed(2);
          return {
            q: `What is ${perc}% of ${val}?`,
            a: ans,
            distract: [
              (parseFloat(ans) + 1).toFixed(2),
              Math.max(0, parseFloat(ans) - 1).toFixed(2)
            ]
          };
        }
      }
    ];
  } else if (topic === "geometry") {
    return [
      {
        question: () => {
          const l = Math.floor(Math.random() * 10) + 2;
          const w = Math.floor(Math.random() * 10) + 2;
          const area = l * w;
          return {
            q: `A rectangle is ${l}cm × ${w}cm. What is its area in cm²?`,
            a: area,
            distract: [area + 2, l + w]
          };
        }
      },
      {
        question: () => {
          const l = Math.floor(Math.random() * 5) + 2;
          const w = Math.floor(Math.random() * 5) + 2;
          const h = Math.floor(Math.random() * 5) + 2;
          const vol = l * w * h;
          return {
            q: `Volume of a ${l}×${w}×${h} rectangular prism (in cm³)?`,
            a: vol,
            distract: [vol + 1, l + w + h]
          };
        }
      },
      {
        question: () => {
          const angleSet = [
            ["Right angle", 90],
            ["Straight line", 180],
            ["Full circle", 360]
          ];
          const pick = angleSet[Math.floor(Math.random() * angleSet.length)];
          return {
            q: `How many degrees in a ${pick[0]}?`,
            a: pick[1],
            distract: [pick[1] + 30, pick[1] - 30]
          };
        }
      }
    ];
  } else {
    // final summary fallback
    return [
      {
        question: () => ({
          q: "12 × 3 = ?",
          a: 36,
          distract: [33, 39]
        })
      },
      {
        question: () => ({
          q: "What’s 25% of 40?",
          a: 10,
          distract: [8, 12]
        })
      },
      {
        question: () => ({
          q: "Area of 5×3 rectangle?",
          a: 15,
          distract: [8,18]
        })
      }
    ];
  }
}

export function generateQuizQuestion(forcedTopic) {
  // if forcedTopic given => pick from that topic only
  // else pick from all => ["ratios","algebra","integers","decimals","geometry","summary"]
  const topics = forcedTopic
    ? [forcedTopic]
    : ["ratios","algebra","integers","decimals","geometry","summary"];

  const topic = topics[Math.floor(Math.random() * topics.length)];
  const pool = getQuestionPool(topic);
  if (!pool || pool.length === 0) {
    return {
      question: "2 + 2=?",
      correctAnswer: 4,
      options: [4, 3, 5]
    };
  }
  // pick a random question from the pool
  const chosen = pool[Math.floor(Math.random() * pool.length)];
  const qObj = chosen.question();
  if (!qObj || !qObj.q || qObj.a === undefined) {
    return {
      question: "2 + 2=?",
      correctAnswer: 4,
      options: [4, 3, 5]
    };
  }
  const distract = qObj.distract || [];
  const options = [qObj.a, ...distract].sort(() => Math.random() - 0.5);

  return {
    question: qObj.q,
    correctAnswer: qObj.a,
    options
  };
}